import * as React from 'react';
const { useState, useEffect } = React;
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLoading } from '@/contexts/LoadingContext';
import { logger } from '@/utils/logger';

interface LoadingTimeoutHandlerProps {
  children: React.ReactNode;
  timeout?: number; // milliseconds
  onTimeout?: () => void;
}

export const LoadingTimeoutHandler: React.FC<LoadingTimeoutHandlerProps> = ({
  children,
  timeout = 30000, // 30 seconds default
  onTimeout
}) => {
  const [isTimedOut, setIsTimedOut] = useState(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const { loadingStates, clearAllLoading } = useLoading();

  // Check if any loading states are active
  const hasActiveLoading = Object.values(loadingStates).some(state => state.isLoading);

  useEffect(() => {
    if (hasActiveLoading && !isTimedOut) {
      // Start timeout timer
      const id = setTimeout(() => {
        logger.warn('Loading timeout reached after', timeout, 'ms');
        setIsTimedOut(true);
        onTimeout?.();
      }, timeout);
      
      setTimeoutId(id);

      return () => {
        if (id) clearTimeout(id);
      };
    } else if (!hasActiveLoading && timeoutId) {
      // Clear timeout if loading finished
      clearTimeout(timeoutId);
      setTimeoutId(null);
      setIsTimedOut(false);
    }
  }, [hasActiveLoading, timeout, onTimeout]);

  const handleResetTimeout = () => {
    setIsTimedOut(false);
    clearAllLoading();
  };

  const handleForceRefresh = () => {
    window.location.reload();
  };

  if (isTimedOut) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card border border-border rounded-lg p-6 text-center space-y-4">
          <div className="flex justify-center">
            <AlertCircle className="h-12 w-12 text-warning" />
          </div>
          
          <div className="space-y-2">
            <h2 className="text-lg font-semibold text-foreground">
              Loading is taking longer than expected
            </h2>
            <p className="text-sm text-muted-foreground">
              The application may be experiencing connectivity issues or high load.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button
              onClick={handleResetTimeout}
              variant="default"
              size="sm"
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Try Again
            </Button>
            
            <Button
              onClick={handleForceRefresh}
              variant="outline"
              size="sm"
            >
              Refresh Page
            </Button>
          </div>

          <div className="text-xs text-muted-foreground">
            <p>If this problem persists, please check your internet connection.</p>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};